/**
 * Custom Audio Player
 * Accessible and performant audio player with lazy loading
 */
export class AudioPlayer {
  constructor(containerId, audioUrl) {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.warn(`AudioPlayer: Container #${containerId} not found`);
      return;
    }

    this.audioUrl = audioUrl;
    this.sourceInjected = false;

    // Cache DOM elements
    this.elements = {
      audio: this.container.querySelector('#audioEl'),
      toggle: this.container.querySelector('#audioToggle'),
      playIcon: this.container.querySelector('#iconPlay'),
      pauseIcon: this.container.querySelector('#iconPause'),
      seek: this.container.querySelector('#audioSeek'),
      progress: this.container.querySelector('#audioProgress'),
      thumb: this.container.querySelector('#audioThumb'),
      fill: this.container.querySelector('#audioFill')
    };

    // Verify required elements exist
    if (!this.validateElements()) {
      console.warn('AudioPlayer: Required elements missing');
      return;
    }

    this.init();
  }

  validateElements() {
    return Object.values(this.elements).every(el => el !== null);
  }

  init() {
    // Bind event handlers
    this.elements.toggle.addEventListener('click', () => this.togglePlayPause());
    this.elements.audio.addEventListener('play', () => this.setPlayUI(true));
    this.elements.audio.addEventListener('pause', () => this.setPlayUI(false));
    this.elements.audio.addEventListener('timeupdate', () => this.updateBar());
    this.elements.audio.addEventListener('loadedmetadata', () => this.updateBar());
    this.elements.audio.addEventListener('ended', () => this.handleEnded());

    // Seek handler (accessible input range)
    this.elements.seek.addEventListener('input', (e) => this.handleSeek(e));

    // Initialize UI
    this.updateBar();
  }

  ensureSource() {
    if (this.sourceInjected) return;

    const source = document.createElement('source');
    source.src = this.audioUrl;
    source.type = 'audio/mpeg';
    this.elements.audio.appendChild(source);
    this.elements.audio.load();
    this.sourceInjected = true;
  }

  async togglePlayPause() {
    if (!this.sourceInjected) {
      this.ensureSource();
    }

    if (this.elements.audio.paused) {
      try {
        await this.elements.audio.play();
      } catch (error) {
        console.warn('AudioPlayer: Play failed', error);
      }
    } else {
      this.elements.audio.pause();
    }
  }

  setPlayUI(playing) {
    this.elements.playIcon.classList.toggle('hidden', playing);
    this.elements.pauseIcon.classList.toggle('hidden', !playing);
    this.elements.toggle.setAttribute('aria-label', playing ? 'Pause' : 'Lecture');
  }

  updateBar() {
    const ratio = this.elements.audio.duration
      ? this.elements.audio.currentTime / this.elements.audio.duration
      : 0;

    const pct = `${ratio * 100}%`;
    const value = Math.floor(ratio * 100);

    // Update UI elements
    this.elements.seek.value = value;
    this.elements.progress.style.width = pct;
    this.elements.fill.style.width = pct;
    this.elements.thumb.style.left = pct;
    this.elements.thumb.style.transform = 'translateX(-50%)';
  }

  handleSeek(event) {
    const ratio = parseInt(event.target.value) / 100;

    if (!this.sourceInjected) {
      this.ensureSource();
      this.elements.audio.addEventListener('loadedmetadata', () => {
        this.elements.audio.currentTime = (this.elements.audio.duration || 0) * ratio;
        this.updateBar();
      }, { once: true });
    } else {
      this.elements.audio.currentTime = (this.elements.audio.duration || 0) * ratio;
    }
  }

  handleEnded() {
    this.elements.audio.currentTime = 0;
    this.setPlayUI(false);
    this.updateBar();
  }

  destroy() {
    // Clean up event listeners if needed
    this.elements.audio.pause();
    this.elements.audio.src = '';
  }
}

// Export pour utilisation globale
if (typeof window !== 'undefined') {
  window.AudioPlayer = AudioPlayer;
}
